"use client";

/* ───────────────────────────────────────────────────────────────────────────
 * Fathom
 *
 * An endless forward approach on a pure-black stage. The camera dollies through
 * a deep volume of suspended, two-sided PHOTO CARDS scattered without order.
 * Each fades in at its own random anchor SOMEWHERE ACROSS THE FRAME — not from
 * one central vanishing point — then grows and sharpens in place as it dollies
 * toward the lens, before fading out and recycling to a fresh spot. Deliberately
 * sparse — only ~4-5 cards on screen at once, at varied sizes. Loops forever.
 *
 * CLICK a card and it FLIPS in true 3D (rotateY) to reveal a NOTE on its back —
 * and keeps flying, so you read the note as the card moves; click again to flip
 * back. The flip lives inside the same css `perspective` + `preserve-3d` stage
 * that owns the depth sort, occlusion and foreshortening. (Opacity + DoF blur are
 * applied to the two FACES, never the flip container, so the 3D never flattens.)
 *
 * One raw-rAF clock owns the dolly, the depth envelopes, and the flip spring;
 * GSAP owns only the field's fade-in. Pure black in every theme, pauses off-
 * screen and in hidden tabs, honours prefers-reduced-motion (a composed still).
 * ─────────────────────────────────────────────────────────────────────────── */

import { useEffect, useRef, useState } from "react";
import { gsap } from "@/lib/animations/gsap-setup";

/* ── stage palette — theme-aware (follows the site's light/dark toggle via <html data-theme>) ── */
interface Palette {
  bg: string; // stage background
  glow: string; // structural (kept transparent)
  vignette: string; // structural (kept transparent)
  title: string; // the centred wordmark colour
}
const DARK: Palette = {
  bg: "#000000",
  glow: "transparent",
  vignette: "transparent",
  title: "rgba(255,255,255,0.92)",
};
const LIGHT: Palette = {
  bg: "#f4f2ec", // soft warm off-white — a gallery-wall light mode
  glow: "transparent",
  vignette: "transparent",
  title: "rgba(18,17,16,0.9)",
};
/** Read the site's resolved theme off <html data-theme> ("light" | "dark"). */
function readTheme(): Palette {
  if (typeof document !== "undefined" && document.documentElement.getAttribute("data-theme") === "light") {
    return LIGHT;
  }
  return DARK;
}

/* drop + contact shadow + hairline keyline — shared by both card faces */
const FACE_SHADOW =
  "0 34px 66px -26px rgba(0,0,0,0.7), 0 10px 24px -14px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.1)";

/* ── the image set. Buyer-swappable: upload replacements over the same storage keys
 *    (sections/fathom/NN.jpg) and set the count — no code change needed.
 *
 *    URLs are SAME-ORIGIN and resolved per request by /api/media, which redirects to
 *    wherever the storage bucket currently lives. The CDN host is derived from the
 *    project name and changes on every rename/clone/domain move, so it is deliberately
 *    never baked into this bundle: no hardcoded host, and nothing resolved once at
 *    build time. Change the domain as often as you like and these keep resolving. ── */
const IMAGE_COUNT = 21;
const MEDIA_BASE = "/api/media";

const buildImages = (cdnBase: string = MEDIA_BASE): string[] =>
  Array.from({ length: IMAGE_COUNT }, (_, i) => {
    const name = `${String(i + 1).padStart(2, "0")}.jpg`;
    return `${cdnBase.replace(/\/+$/, "")}/sections/fathom/${name}`;
  });

/* ── the note on the BACK of each card (paired with the image by index) ── */
interface Note {
  title: string;
  body: string;
}
const NOTES: Note[] = [
  { title: "Match Point", body: "A grin spelled out in tennis balls on the blue, sneakers for punctuation." },
  { title: "Cathedral", body: "Sun breaking through the canopy in solid columns, the forest holding still." },
  { title: "Frosted", body: "A figure passing behind clouded glass, softened to a rumour." },
  { title: "Daffodil", body: "One bloom tucked at the shoulder, a glance held back over it." },
  { title: "Crossing", body: "Five abreast into the low sun, the whole street gone to gold." },
  { title: "Threshold", body: "A stride through a red doorway, light pooling warm on the stone." },
  { title: "Ultraviolet", body: "A blue bloom lit from within against a furnace of orange." },
  { title: "Courtship", body: "Wine-dark callas laid across a green fender, someone about to arrive." },
  { title: "Curbside", body: "A small figure and a red case, the morning traffic streaming past." },
  { title: "Crimson", body: "A red cap catching the last of the light at the crossing." },
  { title: "Kickabout", body: "The whole field breaks for the ball, the hills going pink behind them." },
  { title: "Flame", body: "A calla lily curling like a held note, lit from within." },
  { title: "Honeyed", body: "Honeycomb up close, gold cells beaded with trapped light." },
  { title: "Scarlet", body: "A profile cut clean against a wall of red, cool in the heat." },
  { title: "Last Light", body: "A silhouette in a doorway, evening pouring in gold past her." },
  { title: "Brownstone", body: "Warm brick and tall windows, the street moving quietly past." },
  { title: "Bench", body: "A figure at rest on a sunlit bench, the evening laid out in gold." },
  { title: "Terracotta", body: "Green tailoring against red rock and a hard blue sky." },
  { title: "Homeward", body: "Walking off into the grass, the sun burning out behind." },
  { title: "Vermillion", body: "A red suit blazing on the desert rock, chin up to the sun." },
  { title: "Slipstream", body: "A rider smeared into the dusk, all speed and no edges." },
];

/* Varied card shapes (base px, desktop) — random per spawn. */
const SHAPES: [number, number][] = [
  [284, 284],
  [300, 300],
  [258, 258],
  [232, 320],
  [214, 300],
  [340, 214],
  [322, 202],
];

/* ── flight config ── */
const CFG = {
  pool: 14, // cards on screen — a full field, kept BELOW the photo count (16) so 2 photos rest off-screen and rotate IN on recycle (images keep mixing) with NO on-screen duplicate
  persp: 1080, // px — CSS perspective
  zFar: -2450, // deepest depth (spawn)
  zNear: 760, // recycle threshold — pushed close to the lens so cards grow large and FLY PAST (off the frame edges) before recycling, instead of vanishing mid-screen (sc ≈ 3.4×)
  // Depth-varying dolly speed: FAST at both extremes (deep + near), SLOW through the
  // middle — cards rush in, ease through the readable centre, then rush out again.
  vFast: 560, // px/s at the deep + near extremes — a strong rush IN (grow fast) and OUT (pass fast)
  vMid: 110, // px/s at the middle — a pronounced SLOW-DOWN / dwell (~5× slower than the extremes)
  midDepth: -60, // tz of the slowest point (~natural size)
  slowRange: 520, // tz distance from midDepth over which speed ramps vMid → vFast
  nearBlurStart: 130, // tz where the exit blur begins (long runway → gradual dissolve)
  nearBlur: 0, // px — exit blur DISABLED (images stay sharp all the way through)
  fadeInEnd: -1420, // depth by which the deep-emergence blur has cleared
  anchorX: 0.64, // horizontal anchor spread (± fraction of viewport width)
  anchorY: 0.6, // vertical anchor spread (± fraction of viewport height)
  jitter: 1.05, // random wander within each grid cell (higher = more scattered / mixed)
  drift: 0.6, // outward drift as a card grows — high, so cards fan out toward the edges and sweep PAST the lens (0 = pure grow-in-place)
  dofBlur: 0, // px — deep depth-of-field blur DISABLED (images come through sharp)
  flipEase: 0.12, // per-frame lerp of the click flip
  mobileScale: 0.64,
} as const;

/* a mutable clone of CFG the rAF loop reads each frame (kept as a ref, static defaults) */
type Cfg = { -readonly [K in keyof typeof CFG]: number };


const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);

/* Build the BACK's inner HTML: a blurred, darkened copy of the FRONT image as the
 * background (so the note's backdrop matches its photo), with the note text on top. */
function backHTML(n: Note, idx: number, cw: number, img: string): string {
  const pad = Math.round(cw * 0.085);
  const label = Math.max(9, Math.round(cw * 0.034));
  const ts = Math.round(cw * 0.08);
  const bs = Math.round(cw * 0.049);
  const gap = Math.round(cw * 0.03);
  const blur = Math.max(8, Math.round(cw * 0.05));
  return (
    // blurred copy of the front photo — expanded past the edges so the blur's soft
    // border is clipped away by the face's overflow:hidden
    `<div style="position:absolute;inset:-14%;background-image:url(${img});background-size:cover;` +
    `background-position:center;filter:blur(${blur}px) saturate(1.15) brightness(0.5);"></div>` +
    // legibility scrim
    `<div style="position:absolute;inset:0;background:linear-gradient(180deg,rgba(8,8,10,0.34),rgba(8,8,10,0.66));"></div>` +
    // note text on top
    `<div style="position:absolute;inset:0;padding:${pad}px;display:flex;flex-direction:column;text-shadow:0 1px 14px rgba(0,0,0,0.55);">` +
    `<div style="display:flex;justify-content:space-between;font-family:var(--font-geist-mono);` +
    `font-size:${label}px;letter-spacing:0.14em;text-transform:uppercase;color:rgba(255,255,255,0.62);">` +
    `<span>N° ${String(idx).padStart(2, "0")}</span><span>Note</span></div>` +
    `<div style="flex:1;min-height:0;overflow:hidden;display:flex;flex-direction:column;justify-content:center;gap:${gap}px;">` +
    `<div style="font-family:var(--font-geist-sans);font-weight:600;color:#fbfaf7;font-size:${ts}px;line-height:1.06;letter-spacing:-0.01em;">${n.title}</div>` +
    `<div style="font-family:var(--font-geist-sans);font-weight:400;color:rgba(245,242,236,0.9);font-size:${bs}px;line-height:1.4;">${n.body}</div>` +
    `</div></div>`
  );
}

interface Slot {
  el: HTMLDivElement; // flip container (preserve-3d)
  front: HTMLDivElement; // image face
  back: HTMLDivElement; // note face
  tz: number;
  px: number; // screen anchor x (from centre)
  py: number; // screen anchor y
  cell: number; // fixed grid cell index — keeps this card in its own region of the screen
  w: number; // base card width (px, after mobile scale) — for hit-testing
  h: number;
  flip: number; // target flip angle (0 or 180)
  flipCur: number; // eased flip angle
  blurOn: boolean;
  imgIdx: number; // index of this card's current image/note (for the click-to-focus detail)
  entryScale: number; // 0→1 on first reveal so each card grows from nothing (else 1)
}

/* Composed-still layout for reduced motion. { i: image/note index, tz, px, py, s: shape index } */
const STILL = [
  { i: 0, tz: 130, px: -400, py: -230, s: 1 },
  { i: 5, tz: -240, px: 340, py: -280, s: 3 },
  { i: 9, tz: -30, px: -110, py: 60, s: 0 },
  { i: 3, tz: 300, px: 440, py: 230, s: 2 },
  { i: 7, tz: -560, px: 130, py: -70, s: 4 },
  { i: 8, tz: -1050, px: -260, py: -330, s: 5 },
];

export default function Fathom({
  className,
  cdnBase,
}: {
  className?: string;
  cdnBase?: string;
}) {
  const rootRef = useRef<HTMLElement>(null); // TALL scroll track (drives progress)
  const sceneRef = useRef<HTMLDivElement>(null); // pinned viewport-sized scene
  const stageRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const [pal, setPal] = useState<Palette>(() => readTheme());
  const [titleShown, setTitleShown] = useState(false); // wordmark fades in once the field reveals
  // click-to-focus detail view: the clicked photo grows into a lightbox with a text panel;
  // the flythrough is paused (pausedRef) while it's open and resumes on dismiss
  const [detail, setDetail] = useState<
    { src: string; title: string; body: string; n: number } | null
  >(null);
  const pausedRef = useRef(false);
  const dismiss = () => {
    setDetail(null);
    pausedRef.current = false;
  };

  // the flight config the rAF loop reads every frame (static defaults; no live UI)
  const cfgRef = useRef<Cfg>({ ...CFG });

  useEffect(() => {
    // every use of the image set lives inside this effect, so it is built here
    // rather than memoised in render — one less hook, and the effect stays
    // self-contained with `cdnBase` as its only external input
    const IMAGES = buildImages(cdnBase);

    const root = rootRef.current; // full-viewport section (captures the scroll gesture)
    const scene = sceneRef.current; // the scene the field renders into
    const stage = stageRef.current;
    const world = worldRef.current;
    const field = fieldRef.current;
    if (!root || !scene || !stage || !world || !field) return;

    const cfg = cfgRef.current;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* preload + DECODE every image up front, keeping the decode promises — the field stays
     * hidden until they resolve (below), so a cold load never flashes half-empty frames */
    const decodes = IMAGES.map((src) => {
      const im = new Image();
      im.src = src;
      return im.decode ? im.decode().catch(() => {}) : Promise.resolve();
    });

    let W = scene.clientWidth;
    let H = scene.clientHeight;
    let isMobile = W < 768;
    const measure = () => {
      W = scene.clientWidth;
      H = scene.clientHeight;
      isMobile = W < 768;
    };
    measure();

    /* ── the recycled pool of two-sided flip cards ── */
    const faceCss = (extra: string) =>
      [
        "position:absolute",
        "inset:0",
        "overflow:hidden",
        "backface-visibility:hidden",
        "-webkit-backface-visibility:hidden",
        `box-shadow:${FACE_SHADOW}`,
        extra,
      ].join(";");

    const makeSlot = (cell: number): Slot => {
      const el = document.createElement("div");
      el.style.cssText = [
        "position:absolute",
        "left:50%",
        "top:50%",
        "transform-origin:50% 50%",
        "transform-style:preserve-3d",
        "will-change:transform",
        // clicks are hit-tested on the root instead (a deep card sits behind the
        // flat field plane, so real per-element hit-testing misses it)
        "pointer-events:none",
      ].join(";");
      const front = document.createElement("div");
      front.style.cssText = faceCss("background-size:cover;background-position:center");
      const back = document.createElement("div");
      back.style.cssText = faceCss("background:#141517;transform:rotateY(180deg)");
      el.appendChild(front);
      el.appendChild(back);
      field.appendChild(el);
      return {
        el,
        front,
        back,
        tz: 0,
        px: 0,
        py: 0,
        cell,
        w: 0,
        h: 0,
        flip: 0,
        flipCur: 0,
        blurOn: false,
        imgIdx: 0,
        entryScale: 1,
      };
    };

    const slots: Slot[] = [];
    for (let i = 0; i < cfg.pool; i++) slots.push(makeSlot(i));

    // paint a given image (+ its paired note) onto a card
    const setImg = (s: Slot, idx: number) => {
      s.imgIdx = idx;
      s.front.style.backgroundImage = `url(${IMAGES[idx]})`;
      s.back.innerHTML = backHTML(NOTES[idx % NOTES.length], idx + 1, s.w, IMAGES[idx]);
    };
    // give the card a fresh random SHAPE + the requested image
    const dress = (s: Slot, idx: number) => {
      const scale = isMobile ? cfg.mobileScale : 1;
      const [bw, bh] = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      const cw = Math.round(bw * scale);
      const ch = Math.round(bh * scale);
      s.el.style.width = `${cw}px`;
      s.el.style.height = `${ch}px`;
      s.el.style.marginLeft = `${-cw / 2}px`;
      s.el.style.marginTop = `${-ch / 2}px`;
      s.w = cw;
      s.h = ch;
      setImg(s, idx);
    };

    // place the card at a fresh anchor for the given grid `cell`, showing image `idx`.
    // STRATIFIED anchor: the cell owns a region of a grid over the viewport and the card
    // wanders (jitters) inside it — so cards stay spread across the whole screen instead of
    // clumping. On recycle we hand the card a RANDOM cell + a RANDOM image, so the same photo
    // never keeps returning to the same spot — fresh images arrive in fresh places.
    // a grid deliberately FINER than the card count — ~2.6× as many cells as cards — so a
    // recycled card draws its new anchor from many more possible positions than there are
    // cards. Positions keep changing and spreading across the whole frame instead of cycling
    // the same few spots. Shape adapts to the viewport aspect.
    const gridDims = () => {
      const target = Math.max(cfg.pool + 6, Math.round(cfg.pool * 2.6));
      const cols = Math.max(3, Math.round(Math.sqrt(target * (W / H))));
      const rows = Math.max(3, Math.ceil(target / cols));
      return { cols, rows, total: cols * rows };
    };

    const scatter = (s: Slot, idx: number, cell = s.cell) => {
      s.cell = cell;
      const { cols, rows } = gridDims();
      const col = cell % cols;
      const row = Math.floor(cell / cols) % rows;
      const cx = ((col + 0.5) / cols) * 2 - 1; // cell centre in [-1,1]
      const cy = ((row + 0.5) / rows) * 2 - 1;
      const jx = (Math.random() * 2 - 1) * (1 / cols) * cfg.jitter;
      const jy = (Math.random() * 2 - 1) * (1 / rows) * cfg.jitter;
      const nx = Math.max(-1, Math.min(1, cx + jx));
      const ny = Math.max(-1, Math.min(1, cy + jy));
      s.px = nx * W * cfg.anchorX;
      s.py = ny * H * cfg.anchorY;
      s.flip = 0; // recycled cards come back image-side up
      s.flipCur = 0;
      dress(s, idx);
    };

    // recycle a card that has looped past an edge. Pick both its new IMAGE and its new grid
    // CELL from those NOT currently used by any OTHER card — so the field never shows the same
    // photo twice at once and never stacks two cards in the same region (with pool ≤ image
    // count this keeps every card distinct AND spread; falls back to random if over-subscribed).
    const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
    // the last few RETIRED photos — avoided on the next picks so a photo that just left
    // doesn't immediately flash back in (evens out the rotation over all 10 images).
    const recentImgs: number[] = [];
    // how far a grid cell's centre is from the screen centre (0 = middle, ~1.4 = corner)
    const cellDist = (cell: number) => {
      const { cols, rows } = gridDims();
      const col = cell % cols;
      const row = Math.floor(cell / cols) % rows;
      return Math.hypot(((col + 0.5) / cols) * 2 - 1, ((row + 0.5) / rows) * 2 - 1);
    };
    const recycle = (s: Slot) => {
      // IMAGE: exclude the photos other cards currently show (no on-screen duplicate), then
      // PREFER one that's neither this card's current image NOR one that just retired — so
      // photos rotate EVENLY across all 10 instead of the same few cycling back.
      const usedImg = new Set<number>();
      const usedCell = new Set<number>(); // exclude ALL cells in use INCLUDING this card's own,
      for (const o of slots) {            // so the card always relocates to a different spot
        if (o !== s) usedImg.add(o.imgIdx);
        usedCell.add(o.cell);
      }
      const freeImgs: number[] = [];
      for (let i = 0; i < IMAGES.length; i++) if (!usedImg.has(i)) freeImgs.push(i);
      let idx: number;
      if (freeImgs.length) {
        // some photos are OFF screen — rotate a fresh one in (prefer not self, not just-retired)
        let imgPool = freeImgs.filter((i) => i !== s.imgIdx && !recentImgs.includes(i));
        if (!imgPool.length) imgPool = freeImgs.filter((i) => i !== s.imgIdx);
        if (!imgPool.length) imgPool = freeImgs;
        idx = pick(imgPool);
      } else {
        // more cards than photos → a repeat is unavoidable. Duplicate the image whose current
        // copy is FARTHEST in depth from this spawn (s.tz is already the new deep/near anchor),
        // so the two copies never share the readable near-zone — no visible "same twice".
        let bestDist = -1;
        idx = s.imgIdx;
        for (const o of slots) {
          if (o === s) continue;
          const d = Math.abs(o.tz - s.tz);
          if (d > bestDist) {
            bestDist = d;
            idx = o.imgIdx;
          }
        }
      }
      recentImgs.push(s.imgIdx); // the photo LEAVING now waits before it can return
      while (recentImgs.length > 2) recentImgs.shift();

      // CELL: draw from the FULL fine grid, and bias toward the OUTER frame (best of two
      // random samples) so recycled cards stop landing dead-centre and clumping there.
      const { total } = gridDims();
      const freeCells: number[] = [];
      for (let c = 0; c < total; c++) if (!usedCell.has(c)) freeCells.push(c);
      let cell: number;
      if (freeCells.length) {
        const a = pick(freeCells);
        const b = pick(freeCells);
        cell = cellDist(a) >= cellDist(b) ? a : b;
      } else {
        cell = Math.floor(Math.random() * total);
      }
      scatter(s, idx, cell);
      // the NEW image GROWS IN from nothing (same feel as the load entrance) instead of
      // just appearing — so every recycled photo scales up into place mid-scroll, not a pop.
      s.entryScale = 0;
      gsap.to(s, {
        entryScale: 1,
        duration: 0.7,
        ease: "back.out(1.4)",
        overwrite: "auto",
      });
    };

    const applyTheme = () => setPal(readTheme());
    const themeObs = new MutationObserver(applyTheme);
    themeObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    // pin the card to its anchor and grow in place; append the flip rotateY
    const setTransform = (s: Slot) => {
      const sc = cfg.persp / (cfg.persp - s.tz);
      const drift = 1 + cfg.drift * (sc - 1);
      const planeX = (s.px * drift) / sc;
      const planeY = (s.py * drift) / sc;
      s.el.style.transform =
        `translate3d(${planeX.toFixed(1)}px,${planeY.toFixed(1)}px,${s.tz.toFixed(1)}px) ` +
        `rotateY(${s.flipCur.toFixed(2)}deg) scale(${s.entryScale.toFixed(3)})`;
    };

    /* ── reduced motion: freeze a composed field and stop ── */
    if (reduce) {
      world.style.transform = "rotateX(2deg) rotateY(-3deg)";
      const scale = isMobile ? cfg.mobileScale : 1;
      STILL.forEach((c, k) => {
        const s = slots[k];
        const [bw, bh] = SHAPES[c.s % SHAPES.length];
        const cw = Math.round(bw * scale);
        const ch = Math.round(bh * scale);
        s.el.style.width = `${cw}px`;
        s.el.style.height = `${ch}px`;
        s.el.style.marginLeft = `${-cw / 2}px`;
        s.el.style.marginTop = `${-ch / 2}px`;
        const idx = c.i % IMAGES.length;
        s.imgIdx = idx;
        s.front.style.backgroundImage = `url(${IMAGES[idx]})`;
        s.back.innerHTML = backHTML(NOTES[idx % NOTES.length], idx + 1, cw, IMAGES[idx]);
        s.tz = c.tz;
        s.px = c.px * scale;
        s.py = c.py * scale;
        setTransform(s);
      });
      gsap.fromTo(stage, { opacity: 0 }, { opacity: 1, duration: 0.9, ease: "power2.out" });
      setTitleShown(true);
      return () => {
        themeObs.disconnect();
        slots.forEach((s) => s.el.remove());
      };
    }

    /* ── click to flip — hit-test on the root so ANY card is clickable wherever it
     *    is (a deep card sits BEHIND the flat field plane, so real per-element
     *    hit-testing would miss it). We project each card's on-screen rect and pick
     *    the front-most one under the pointer. EVERY card is clickable regardless of
     *    depth/size: deep cards are included, and each card's clickable box is floored
     *    at MIN_HIT so even a pin-sized far card keeps a comfortable target. Because
     *    the padding only ever grows a box that's already smaller than the floor (a
     *    real, larger card keeps its true rect) and the NEAREST card under the pointer
     *    wins, this never steals a click meant for a big foreground photo. ── */
    const MIN_HIT = 26; // px — floor on each card's clickable half-extent (≈52px target)
    const hitTest = (mx: number, my: number): Slot | null => {
      let best: Slot | null = null;
      let bestTz = -Infinity;
      for (const s of slots) {
        const tz = s.tz;
        const sc = cfg.persp / (cfg.persp - tz);
        const d = 1 + cfg.drift * (sc - 1);
        const cx = W / 2 + s.px * d;
        const cy = H / 2 + s.py * d;
        const hw = Math.max(MIN_HIT, (s.w * sc) / 2);
        const hh = Math.max(MIN_HIT, (s.h * sc) / 2);
        if (mx >= cx - hw && mx <= cx + hw && my >= cy - hh && my <= cy + hh && tz > bestTz) {
          bestTz = tz;
          best = s;
        }
      }
      return best;
    };
    const onClick = (e: MouseEvent) => {
      if (pausedRef.current) return; // a detail is already open — the overlay handles clicks
      const r = scene.getBoundingClientRect();
      const s = hitTest(e.clientX - r.left, e.clientY - r.top);
      if (s) {
        // FREEZE the flythrough and open the clicked photo in the focus/detail view
        const note = NOTES[s.imgIdx % NOTES.length];
        pausedRef.current = true;
        setDetail({ src: IMAGES[s.imgIdx], title: note.title, body: note.body, n: s.imgIdx + 1 });
      }
    };
    // NOTE: no cursor-swap on hover — the pointer stays the normal arrow everywhere.
    // (Every card is clickable now, so a hover hint would light up most of the screen.)
    // Click-to-focus is DISABLED for now — flip to true to re-enable the detail view.
    const CLICK_TO_FOCUS = false;
    if (CLICK_TO_FOCUS) scene.addEventListener("click", onClick);

    /* ── seed the pool, spread across depth with a random jitter (uneven cadence). The field
     *    is HIDDEN until the images decode (revealed in the gate below), so a cold load never
     *    shows empty frames — the dolly runs meanwhile, so it reveals already in motion. ── */
    field.style.visibility = "hidden";
    const span = cfg.zNear - cfg.zFar;
    // Seed: one UNIQUE image per card (idx=i), spread one-per-band across the full depth
    // [zFar, zNear] so the field is full & continuous from the first frame. From here on the
    // scroll gesture drives depth, and cards recycle (fresh image + fresh position) each time
    // they loop past an edge — so you never see the same photo in the same place twice.
    const seedTotal = gridDims().total;
    slots.forEach((s, i) => {
      // spread the initial cards ACROSS the whole fine grid (not the first N cells) so the
      // opening field is scattered wide, then recycles keep remixing images + positions
      const cell = Math.floor(((i + 0.5) * seedTotal) / slots.length);
      scatter(s, i % IMAGES.length, cell);
      s.tz = cfg.zFar + ((i + Math.random()) / slots.length) * span;
    });

    let running = false;
    let raf = 0;
    let revealed = false; // held until images decode (§reveal gate) — first frame = advance 0

    /* ── the dolly AUTO-PLAYS forever; the SCROLL GESTURE rides ON TOP of it. Each frame the
     *    field advances by AUTO_DZ on its own (so it keeps flying with no input), and we ALSO
     *    capture wheel + touch-drag into `targetAdvance` — the loop eases `advance` toward it
     *    and adds that eased step to the auto step, so while you scroll the motion follows your
     *    scroll, and when you stop it settles back to the steady auto glide. Cards that cross the
     *    near/far edge RECYCLE (fresh image + position), so it flies infinitely either way. ── */
    const AUTO_DZ = 2.4; // px of forward dolly travel per frame with no input (~144px/s @60fps)
    const SCROLL_SCALE = 3.2; // px of dolly travel per px of wheel/drag delta — higher = each
    // scroll pushes MORE of the field forward (one full deep→near pass in ~10 wheel notches)
    const SCROLL_EASE = 0.14; // per-frame lerp toward the target (smoothing / inertia)
    let advance = 0;
    let targetAdvance = 0;
    let settled = false;
    const scrollBy = (deltaPx: number) => {
      if (pausedRef.current) return; // frozen while a detail is open
      targetAdvance += deltaPx * SCROLL_SCALE;
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault(); // this gesture drives the flythrough, not a page scroll
      scrollBy(e.deltaY);
    };
    // touch drag → same accumulation (finger up = scroll down = forward)
    let touchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY ?? touchY;
      scrollBy(touchY - y);
      touchY = y;
      if (e.cancelable) e.preventDefault();
    };

    // advance every card by the eased scroll step, recycling any that cross an edge (fresh
    // image + position). `while` guards a single huge step (a fast fling) crossing >1 span.
    const step = (dz: number) => {
      for (const s of slots) {
        s.tz += dz;
        while (s.tz > cfg.zNear) {
          s.tz -= span;
          recycle(s);
        }
        while (s.tz < cfg.zFar) {
          s.tz += span;
          recycle(s);
        }
        s.flipCur += (s.flip - s.flipCur) * cfg.flipEase; // ease the click flip
        render(s);
      }
    };

    // blur + transform for one card. NO opacity — cards are always fully opaque; they
    // emerge soft from the deep (blur) and, near the lens, blur + zip out fast (§frame).
    // Split out so the seeded frame can be painted ONCE up-front (the page loads straight
    // into the live animation).
    const render = (s: Slot) => {
      const tz = s.tz;
      // soft at BOTH extremes, sharp through the readable middle
      let blur = 0;
      if (tz < cfg.fadeInEnd) {
        blur = (1 - (tz - cfg.zFar) / (cfg.fadeInEnd - cfg.zFar)) * cfg.dofBlur; // deep emergence
      } else if (tz > cfg.nearBlurStart) {
        // eased (t³) so the card stays sharp most of the way, then dissolves into a soft
        // blur right at the end — the recycle happens on a hazy card, so the cut is smooth
        const t = clamp01((tz - cfg.nearBlurStart) / (cfg.zNear - cfg.nearBlurStart));
        blur = t * t * t * cfg.nearBlur;
      }
      if (blur > 0.15) {
        const f = `blur(${blur.toFixed(2)}px)`;
        s.front.style.filter = f;
        s.back.style.filter = f;
        s.blurOn = true;
      } else if (s.blurOn) {
        s.front.style.filter = "none";
        s.back.style.filter = "none";
        s.blurOn = false;
      }
      // size comes PURELY from perspective (persp/(persp−tz)) — no extra scale factor, so an
      // image genuinely MOVES FORWARD through depth (tiny & far → large & near), never a
      // superimposed "scale-in" animation.
      setTransform(s);
    };

    // paint the seeded state immediately, so the first frame IS the running animation
    slots.forEach(render);

    function frame() {
      if (!running) return;
      // FREEZE the field while a detail view is open (resumes seamlessly on dismiss)
      if (pausedRef.current) {
        raf = requestAnimationFrame(frame);
        return;
      }
      let dz = AUTO_DZ; // baseline auto-glide — the field keeps flying with no input
      const dist = targetAdvance - advance;
      if (Math.abs(dist) < 0.08) {
        // scroll settled — reset the scroll accumulators to 0 so endless scrolling never drifts
        // into large floats (the auto-glide still runs; the cards' tz hold their true positions)
        if (!settled) {
          advance = 0;
          targetAdvance = 0;
          settled = true;
        }
      } else {
        settled = false;
        const sdz = dist * SCROLL_EASE; // eased scroll step this frame — rides on top of auto
        advance += sdz;
        dz += sdz;
      }
      step(dz); // move + recycle-on-wrap + render every card (auto + any scroll)
      raf = requestAnimationFrame(frame);
    }

    /* ── pause off-screen / hidden ── */
    const start = () => {
      if (running || !revealed) return; // held until the decode gate reveals the field
      running = true;
      settled = false;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };
    const io = new IntersectionObserver(
      ([e]) => (e.isIntersecting ? start() : stop()),
      { threshold: 0 },
    );
    io.observe(root);
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener("visibilitychange", onVis);

    window.addEventListener("resize", measure);
    // the scroll GESTURE (wheel + touch drag) is the only thing that advances the dolly
    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchmove", onTouchMove, { passive: false });

    // reveal the field (+ start the dolly + fade in the title/label) only once the images have
    // DECODED (or after a 1.2 s safety cap) — so the first thing you see is the full field at
    // its deep seeded state, already sharp, then everything moves forward from the distance
    let cancelled = false;
    Promise.race([
      Promise.all(decodes),
      new Promise((r) => setTimeout(r, 1200)),
    ]).then(() => {
      if (cancelled) return;
      revealed = true;
      // paint the scale-0 state while still HIDDEN so there's no full-size flash on reveal
      slots.forEach((s) => (s.entryScale = 0));
      slots.forEach(render);
      field.style.visibility = "visible";
      // entrance: every card GROWS from scale 0 (staggered at random, so the field
      // materialises organically rather than popping in), while the whole field also glides
      // gently FORWARD from depth. Both ride on top of the auto-dolly.
      gsap.to(slots, {
        entryScale: 1,
        duration: 0.9,
        ease: "back.out(1.4)",
        stagger: { each: 0.06, from: "random" },
      });
      gsap.fromTo(world, { z: -700 }, { z: 0, duration: 1.8, ease: "power2.out" });
      start();
      setTitleShown(true);
    });

    return () => {
      cancelled = true;
      stop();
      io.disconnect();
      themeObs.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("resize", measure);
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      if (CLICK_TO_FOCUS) scene.removeEventListener("click", onClick);
      slots.forEach((s) => s.el.remove());
    };
  }, [cdnBase]);

  return (
    <section
      ref={rootRef}
      className={`relative h-dvh w-full overflow-hidden ${className || ""}`}
      style={{ backgroundColor: pal.bg, transition: "background-color 0.5s", touchAction: "none" }}
    >
      {/* full-viewport scene — the whole flythrough renders here. The dolly auto-plays;
          scrolling (wheel/drag) rides on top to drive it faster; the field itself never scrolls. */}
      <div ref={sceneRef} className="absolute inset-0 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: pal.glow }}
        aria-hidden
      />

      <div
        ref={stageRef}
        className="absolute inset-0"
        style={{ perspective: `${CFG.persp}px`, perspectiveOrigin: "50% 50%" }}
      >
        <div
          ref={worldRef}
          className="absolute inset-0"
          style={{ transformStyle: "preserve-3d", transformOrigin: "50% 50%" }}
        >
          {/* the recycled flip-card pool mounts here */}
          <div
            ref={fieldRef}
            className="absolute inset-0"
            style={{ transformStyle: "preserve-3d" }}
          />

        </div>
      </div>

      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: pal.vignette }}
        aria-hidden
      />

      {/* section title — a 2D overlay pinned ABOVE the flying images so nothing
          ever occludes it (higher z-index than the whole 3D field); fades up
          from transparent on load */}
      <div
        ref={titleRef}
        className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
        style={{
          zIndex: 30,
          color: pal.title, // theme-aware; changes with the light/dark toggle
          opacity: titleShown ? 1 : 0,
          // fade IN on load (2.6s), but recolour QUICKLY when the theme flips (0.6s)
          transition: "opacity 2.6s ease-out, color 0.6s ease",
          fontFamily: "var(--font-cyrene), var(--font-instrument-serif), sans-serif",
          fontWeight: 400,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          lineHeight: 0.98,
          textAlign: "center",
          whiteSpace: "nowrap",
          willChange: "opacity, color",
        }}
        aria-hidden
      >
        <span style={{ fontSize: "clamp(1.55rem, 5.6vw, 5.6rem)" }}>Stories</span>
        <span style={{ fontSize: "clamp(1.55rem, 5.6vw, 5.6rem)" }}>In Motion</span>
      </div>

      {/* keyframes + responsive rules for the click-to-focus detail view */}
      <style>{`
        @keyframes fathomDetailFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fathomDetailGrow { from { opacity: 0; transform: scale(0.8) } to { opacity: 1; transform: scale(1) } }
        @keyframes fathomDetailText { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        @media (max-width: 760px) {
          .fathom-detail { flex-direction: column !important; flex-wrap: nowrap !important; }
          .fathom-detail img { max-width: 88vw !important; max-height: 46vh !important; }
          .fathom-detail-panel { max-width: 88vw !important; }
        }
      `}</style>

      {/* click-to-focus DETAIL VIEW — the clicked photo grows into a lightbox with a text
          panel; the flythrough is frozen (blurred) behind it and resumes when dismissed */}
      {detail && (
        <div
          className="fathom-detail"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 40,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: "clamp(1.25rem, 4vw, 3.5rem)",
            padding: "clamp(1.5rem, 6vw, 5.5rem)",
            background: "rgba(0,0,0,0.8)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            cursor: "zoom-out",
            animation: "fathomDetailFade 0.3s ease-out both",
          }}
        >
          <img
            src={detail.src}
            alt={detail.title}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: "min(78vh, 820px)",
              maxWidth: "min(52vw, 720px)",
              objectFit: "contain",
              display: "block",
              boxShadow: "0 50px 130px -30px rgba(0,0,0,0.9)",
              cursor: "default",
              animation: "fathomDetailGrow 0.44s cubic-bezier(0.2,0.75,0.2,1) both",
            }}
          />
          <div
            className="fathom-detail-panel"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "min(34vw, 380px)",
              cursor: "default",
              animation: "fathomDetailText 0.5s ease-out 0.05s both",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-geist-mono)",
                fontSize: "0.72rem",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.5)",
              }}
            >
              N° {String(detail.n).padStart(2, "0")} — Street Archive
            </div>
            <h3
              style={{
                fontFamily: "var(--font-instrument-serif)",
                fontWeight: 400,
                fontSize: "clamp(2rem, 4.4vw, 3.4rem)",
                lineHeight: 1.04,
                color: "#f5f3ee",
                margin: "1rem 0 0",
              }}
            >
              {detail.title}
            </h3>
            <p
              style={{
                fontFamily: "var(--font-geist-sans)",
                fontSize: "1rem",
                lineHeight: 1.6,
                color: "rgba(245,242,236,0.72)",
                margin: "1.1rem 0 0",
                maxWidth: "34ch",
              }}
            >
              {detail.body}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismiss();
              }}
              style={{
                marginTop: "1.9rem",
                fontFamily: "var(--font-geist-mono)",
                fontSize: "0.7rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.66)",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.22)",
                borderRadius: "999px",
                padding: "0.6rem 1.3rem",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      </div>
    </section>
  );
}
